import { getCurrentUser, signOut } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
} from '@mui/material';
import { Amplify } from 'aws-amplify';
import { SnackbarProvider } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import {
  apiOrganization,
  apiScope,
  compsFetch,
  selectApiOrganization,
} from './api';
import AuthCallback from './components/AuthCallback';
import MapPage from './components/MapPage';
import ProfileSetup from './components/ProfileSetup';
import SignIn from './components/Signin';
import { awsConfig, COMPS_API_BASE } from './config';
import { authErrorMessage } from './utils/auth-error';

Amplify.configure(awsConfig);
export default function App() {
  const [user, setUser] = useState(null),
    [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true),
    [error, setError] = useState('');
  const [organizations, setOrganizations] = useState([]),
    [organizationId, setOrganizationId] = useState('');
  const [needsProfile, setNeedsProfile] = useState(false),
    [editingProfile, setEditingProfile] = useState(false);
  const generation = useRef(0);
  const loadProfile = async (selection = apiOrganization(), current = user) => {
    const version = ++generation.current;
    setLoading(true);
    setError('');
    setProfile(null);
    selectApiOrganization(selection, current?.userId);
    try {
      const response = await compsFetch(`${COMPS_API_BASE}/user`),
        data = await response.json();
      if (version !== generation.current) return;
      if (!response.ok || !data.success)
        throw new Error(authErrorMessage(data));
      setOrganizations(data.organizations || []);
      const selected = data.user?.organizationId || '';
      selectApiOrganization(selected, current?.userId);
      setOrganizationId(selected);
      setProfile(data.user);
      setNeedsProfile(data.needs_profile_completion || false);
    } catch (failure) {
      if (version === generation.current) {
        selectApiOrganization(null, current?.userId);
        setOrganizationId('');
        setError(failure.message);
      }
    } finally {
      if (version === generation.current) setLoading(false);
    }
  };
  const clear = () => {
    generation.current++;
    selectApiOrganization(null, null);
    setUser(null);
    setProfile(null);
    setOrganizationId('');
    setOrganizations([]);
    setNeedsProfile(false);
    setEditingProfile(false);
  };
  const checkUser = async () => {
    setLoading(true);
    try {
      const current = await getCurrentUser();
      setUser(current);
      await loadProfile(null, current);
    } catch {
      clear();
      setLoading(false);
    }
  };
  const logout = async () => {
    clear();
    await signOut();
  };
  useEffect(() => {
    void checkUser();
    const stop = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        void checkUser();
        if (window.location.pathname === '/auth/callback')
          window.history.replaceState(null, '', '/');
      } else if (payload.event === 'signOut' || payload.event === 'signedOut')
        clear();
    });
    return () => {
      generation.current++;
      stop();
    };
  }, []);
  if (loading)
    return (
      <Box sx={{ p: 4 }}>
        <CircularProgress aria-label='Loading organization' />
      </Box>
    );
  if (!user) {
    if (window.location.pathname === '/auth/callback') return <AuthCallback />;
    return <SignIn />;
  }
  return (
    <SnackbarProvider maxSnack={3}>
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1 }}>
          {organizations.length > 1 && (
            <TextField
              select
              label='Organization'
              value={organizationId}
              onChange={(event) => {
                setEditingProfile(false);
                void loadProfile(event.target.value);
              }}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value='' disabled>
                Select an organization
              </MenuItem>
              {organizations.map((organization) => (
                <MenuItem key={organization.id} value={organization.id}>
                  {organization.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Button onClick={logout}>Sign out</Button>
        </Box>
        {error ? (
          <Alert
            severity='error'
            action={<Button onClick={() => loadProfile()}>Retry</Button>}
          >
            {error}
          </Alert>
        ) : !profile ? (
          <Alert severity='info'>Select an organization to continue.</Alert>
        ) : (
          <Box key={apiScope()} sx={{ flex: 1, minHeight: 0 }}>
            {needsProfile || editingProfile ? (
              <ProfileSetup
                profile={profile}
                onProfileUpdated={() => {
                  setEditingProfile(false);
                  void loadProfile();
                }}
                isEditing={editingProfile}
                onSignOut={logout}
                onCancel={() => setEditingProfile(false)}
              />
            ) : (
              <MapPage
                user={user}
                userData={profile}
                onEditProfile={() => setEditingProfile(true)}
                refreshUserData={() => loadProfile()}
              />
            )}
          </Box>
        )}
      </Box>
    </SnackbarProvider>
  );
}
